import { useState } from "react";
import { ConfirmDialog, Input } from "@v0y4ge/jrni-ui";
import type { NavApi } from "../../shell/router";
import type { UIEvent } from "../../lib/events-adapter";
import { useT } from "../../lib/i18n";
import { eventActions } from "./eventActions";
import { useNav } from "../../shell/router";

/**
 * The confirm step in front of "Cancel event" / "Delete draft" — shared by the
 * Events list row menu and the event drawer menu so both ask the same question.
 * Cancelling a published event notifies its registered guests, so it is never
 * one click. The optional reason is sent with the cancel.
 */
export function CancelEventConfirm({ ev, nav, onClose }: { ev: UIEvent | null; nav: NavApi; onClose: () => void }) {
  const t = useT();
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const isDraft = ev?.status === "draft";
  const nav2 = useNav();
  /* Say only what will happen (CC 0340). The server says whether a cancellation message goes out;
     until it does, the line promises nothing. No guests = nobody to tell, whatever the setting. */
  const count = ev ? (ev.confirmed ?? ev.registered ?? 0) : 0;
  const notice = ev?.cancellationNotice;
  const consequence = !ev ? undefined : isDraft
    ? t("events.cancelConfirm.deleteConsequence", "This draft is removed and cannot be recovered.")
    : count === 0
      ? t("events.cancelConfirm.noGuests", "No one is registered, so no one needs to be told.")
      : notice && notice.sends
        ? t("events.cancelConfirm.consequence", "{count, plural, one {# registered guest will be notified.} other {# registered guests will be notified.}}", { count })
        : notice && !notice.sends
          ? (notice.reason === "switched_off"
            ? t("events.cancelConfirm.noticeOff", "The cancellation message is switched off, so {count, plural, one {the # registered guest} other {the # registered guests}} won't be notified.", { count })
            : t("events.cancelConfirm.noticeMissing", "No cancellation message is set up, so {count, plural, one {the # registered guest} other {the # registered guests}} won't be notified.", { count }))
          : t("events.cancelConfirm.noticeUnknown", "{count, plural, one {# guest is registered.} other {# guests are registered.}} They're notified only if a cancellation message is set up in Messaging.", { count });
  const showMessagingLink = !isDraft && count > 0 && !(notice && notice.sends);
  const close = () => { if (!busy) { setReason(""); onClose(); } };
  const run = async () => {
    if (!ev) return;
    setBusy(true);
    try { await eventActions.cancelOrDiscard(ev, nav, { reason }); } finally { setBusy(false); setReason(""); onClose(); }
  };
  return (
    <ConfirmDialog open={!!ev} tone="danger" busy={busy} testid="event-cancel-confirm"
      title={ev ? (isDraft ? t("events.cancelConfirm.deleteTitle", "Delete draft {title}?", { title: ev.title }) : t("events.cancelConfirm.title", "Cancel {title}?", { title: ev.title })) : ""}
      consequence={consequence}
      confirmLabel={isDraft ? t("events.cancelConfirm.deleteConfirm", "Delete draft") : t("events.cancelConfirm.confirm", "Cancel event")}
      cancelLabel={isDraft ? t("events.cancelConfirm.deleteKeep", "Keep draft") : t("events.cancelConfirm.keep", "Keep event")}
      onCancel={close} onConfirm={() => { void run(); }}>
      {showMessagingLink ? (
        <button type="button" data-testid="event-cancel-messaging-link" onClick={() => { close(); nav2.go("messaging"); }}
          style={{ alignSelf: "flex-start", border: "none", background: "none", padding: 0, font: "inherit", fontSize: 13, fontWeight: 600, color: "var(--jrni-color-primary-text, #5142C7)", textDecoration: "underline", cursor: "pointer", marginBottom: 10 }}>
          {t("events.cancelConfirm.openMessaging", "Set up the cancellation message in Messaging")}
        </button>
      ) : null}
      {isDraft ? null : (
        <Input label={t("events.cancelConfirm.reason", "Reason (optional)")} value={reason} onChange={(e) => setReason(e.target.value)} disabled={busy} testid="event-cancel-reason" />
      )}
    </ConfirmDialog>
  );
}
